// ParentVisitsToday.js
import React, { useEffect, useState } from 'react';
import API from './api';
import { FaBookOpen, FaVideo } from 'react-icons/fa';

/** =========================================================
 * WEATHER helpers (local, based on your W2U rules)
 * ========================================================= */

const W2U_SD_URL = 'https://weather2umbrella.com/san-diego,32.7153,-117.156';

// CRA-compatible dynamic imports from /src/opsWeather
let opsWeatherCtx = null;
try {
  // eslint-disable-next-line
  opsWeatherCtx = require.context('./opsWeather', false, /\.(png|jpg|jpeg|svg)$/);
} catch {
  opsWeatherCtx = null;
}
function opsWeatherIconSrc(filename) {
  try {
    if (!opsWeatherCtx || !filename) return null;
    return opsWeatherCtx(`./${filename}`);
  } catch {
    return null;
  }
}

function toNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function pickNumber(obj, keys) {
  for (const k of keys) {
    const n = toNum(obj?.[k]);
    if (n != null) return n;
  }
  return null;
}

function normalizeIconFilename(raw) {
  if (!raw) return null;
  let s = String(raw).trim();
  if (!s) return null;
  s = s.replace(/^"+|"+$/g, '');
  // remove any path
  s = s.split('/').pop();
  // if no extension, assume png
  if (!/\.(png|jpg|jpeg|svg)$/i.test(s)) s = `${s}.png`;
  return s;
}

// Your alert logic (same thresholds)
function getAlertCode(weather) {
  const iconRaw = String(weather?.icon || '').trim();
  const iconSuffix = iconRaw
    ? iconRaw.replace(/\.(png|jpg|jpeg|svg)$/i, '').substring(1)
    : '';

  const pcp = pickNumber(weather, ['pcp', 'precip', 'precip_mm', 'rain', 'rain_mm']) ?? 0;
  const tmp = pickNumber(weather, ['tmp', 'temp', 'temperature', 't']) ?? 0;
  const ws = pickNumber(weather, ['ws', 'wind', 'wind_speed', 'wind_mps']) ?? 0;
  const rh = pickNumber(weather, ['rh', 'humidity', 'hum']) ?? 0;
  const uv = pickNumber(weather, ['uv', 'uvi']) ?? 0;

  if (iconSuffix === '11' && pcp > 0.8) return 'a04b';
  if (iconSuffix === '13' && pcp > 0.6) return 'a05';
  if ((iconSuffix === '14' || iconRaw.trim() === '15') && tmp < -1) return 'a05';
  if (pcp > 1.5 && tmp > 1.1) return 'a03';
  if (ws > 10) return 'a02';
  if (tmp > 32 && rh > 39) return 'a06';
  if (tmp > 37 && uv > 6.5) return 'a06';
  if (tmp > 36 && uv > 7.0) return 'a01';
  if (tmp > 33 && uv > 9) return 'a01';
  if (uv > 9.0) return 'a01';
  if (tmp < -1 && pcp > 0.5) return 'a04';
  if (ws > 7 && pcp > 1.2) return 'a05';
  return null;
}

// Light-mode temperature coloring (Celsius ranges)
const temperatureRangesLight = [
  { range: [-Infinity, -42], color: 'B224B2' },
  { range: [-41, -40], color: 'A724B2' },
  { range: [-39, -35], color: '9B24B2' },
  { range: [-34, -30], color: '8F24B2' },
  { range: [-29, -28], color: '7E24B2' },
  { range: [-27, -26], color: '6D24B2' },
  { range: [-25, -24], color: '5D24B2' },
  { range: [-23, -22], color: '4C24B2' },
  { range: [-21, -20], color: '432DB2' },
  { range: [-19, -18], color: '332DB2' },
  { range: [-17, -16], color: '2D36B2' },
  { range: [-15, -14], color: '2D45B2' },
  { range: [-13, -12], color: '305BBF' },
  { range: [-11, -10], color: '2666BF' },
  { range: [-9, -8], color: '2678BF' },
  { range: [-7, -6], color: '268ABF' },
  { range: [-5, -4], color: '269CBF' },
  { range: [-3, -2], color: '24A2B2' },
  { range: [-1, 0], color: '21A6A6' },
  { range: [1, 2], color: '21A68B' },
  { range: [3, 4], color: '21A671' },
  { range: [5, 6], color: '21A656' },
  { range: [7, 8], color: '21A63C' },
  { range: [9, 10], color: '21A621' },
  { range: [11, 12], color: '3CA621' },
  { range: [13, 14], color: '4CA611' },
  { range: [15, 16], color: '6AA611' },
  { range: [17, 18], color: '89AB00' },
  { range: [19, 20], color: 'B8B800' },
  { range: [21, 22], color: 'CCA300' },
  { range: [23, 24], color: 'CC7E0A' },
  { range: [25, 26], color: 'CC5E14' },
  { range: [27, 28], color: 'CC3914' },
  { range: [29, 30], color: 'BF1D1D' },
  { range: [31, 32], color: 'C4142B' },
  { range: [33, 34], color: 'C71444' },
  { range: [35, 36], color: 'C9145D' },
  { range: [37, 38], color: 'CC1476' },
  { range: [39, 40], color: 'CC148F' },
  { range: [41, 45], color: 'CC149E' },
  { range: [46, Infinity], color: 'CC14AD' },
];

function getTemperatureColorCelsius(tempC) {
  const t = toNum(tempC);
  if (t == null) return '#111827';
  for (const { range, color } of temperatureRangesLight) {
    if (t >= range[0] && t <= range[1]) return `#${color}`;
  }
  return '#111827';
}

function cToF(tempC) {
  const t = toNum(tempC);
  if (t == null) return null;
  return (t * 9) / 5 + 32;
}

function formatF(tempC) {
  const f = cToF(tempC);
  if (f == null) return '—';
  return `${Math.round(f)}°F`;
}

function parseHourLabel(h) {
  if (!h) return null;
  const s = String(h);

  // "2025-12-24 17:00:00" or ISO "2025-12-24T17:00:00Z"
  const m1 = s.match(/T(\d{2}):/);
  if (m1 && m1[1]) return `${m1[1]}h`;

  const m2 = s.match(/\s(\d{2}):/);
  if (m2 && m2[1]) return `${m2[1]}h`;

  // already hour
  const n = Number(s);
  if (Number.isFinite(n)) return `${String(n).padStart(2, '0')}h`;

  return null;
}

/** =========================================================
 * END WEATHER helpers
 * ========================================================= */

// telehealth badge style
const TELEHEALTH_BADGE_COLOR =
  'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[11px]';

// util
function addMinutesHHMM(hhmm, mins) {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(':').map(Number);
  const d = new Date(2000, 0, 1, h || 0, m || 0, 0, 0);
  d.setMinutes(d.getMinutes() + (mins || 0));
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

function groupByDate(visits) {
  return visits.reduce((acc, v) => {
    const date = v.scheduled_at.slice(0, 10);
    if (!acc[date]) acc[date] = [];
    acc[date].push(v);
    return acc;
  }, {});
}

// status badge
function StatusBadge({ status, children }) {
  let color = 'bg-gray-200 text-gray-700';
  if (status === 'pending') color = 'bg-yellow-100 text-yellow-800';
  if (status === 'accepted') color = 'bg-green-100 text-green-700';
  if (status === 'rejected') color = 'bg-red-100 text-red-700';
  if (status === 'draft') color = 'bg-blue-100 text-blue-700';
  if (status === 'complete') color = 'bg-green-200 text-green-900';
  if (status === 'canceled') color = 'bg-purple-200 text-purple-700';
  return (
    <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${color}`}>
      {children || status}
    </span>
  );
}

// discipline badge (EI/PI/ABA/OT/PT…)
function ProgramBadge({ program_type, discipline_code }) {
  const raw = (discipline_code || program_type || '').toString().trim();
  if (!raw) return null;

  const code = raw.replace(/^PROG_/i, '').toUpperCase();

  let extra = '';
  let label = code;

  if (code === 'PI') {
    extra = ' bg-orange-100 text-orange-800 border border-orange-200';
    label = 'PI';
  } else if (code === 'EI') {
    extra = ' bg-blue-100 text-blue-800 border border-blue-200';
    label = 'EI';
  } else if (code === 'ABA') {
    extra = ' bg-purple-100 text-purple-800 border border-purple-200';
    label = 'ABA';
  } else if (code === 'OT') {
    extra = ' bg-teal-100 text-teal-800 border border-teal-200';
    label = 'OT';
  } else if (code === 'PT') {
    extra = ' bg-emerald-100 text-emerald-800 border border-emerald-200';
    label = 'PT';
  } else if (code === 'ST') {
    extra = ' bg-green-100 text-green-800 border border-green-200';
    label = 'ST';
  } else {
    extra = ' border border-gray-400 text-gray-700 bg-gray-50';
    label = code;
  }

  return (
    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${extra}`}>
      {label}
    </span>
  );
}

function EventBadge({ isEvent }) {
  if (!isEvent) return null;
  return (
    <span className="px-2 py-0.5 rounded text-xs font-semibold bg-yellow-100 text-yellow-800 border border-yellow-300">
      EVENT
    </span>
  );
}

const isEventVisit = (v) =>
  v.type === 'event' || v.visit_type === 'event' || v.is_event === 1 || v.is_event === true;

// URL normalizacija za telehealth link
function normalizeTelehealthLink(link) {
  if (!link) return null;
  const val = link.trim();

  // već ima protokol (http, https, zoommtg, meet, itd.)
  if (/^[a-zA-Z][a-zA-Z0-9+.+-]*:/.test(val)) {
    return val;
  }

  // nema protokol, ali ima tačku -> tretiramo kao domen/URL i dodamo https://
  if (val.includes('.')) {
    return 'https://' + val;
  }

  // fallback – vrati original (možda meeting ID ili custom schema)
  return val;
}

// Telehealth badge + join link (ispod imena deteta)
function TelehealthBadge({ delivery_mode, telehealth_link, showLink = true }) {
  if (delivery_mode !== 'telehealth' && delivery_mode !== 'hybrid') return null;

  const label = delivery_mode === 'telehealth' ? 'Telehealth' : 'Hybrid';
  const href = normalizeTelehealthLink(telehealth_link);

  return (
    <div className="flex items-center gap-1 mt-0.5">
      <span className={TELEHEALTH_BADGE_COLOR}>
        <FaVideo className="w-3 h-3" />
        {label}
      </span>
      {href && showLink && (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[11px] text-emerald-700 underline ml-1"
        >
          Join session
        </a>
      )}
    </div>
  );
}

// samo vreme
function VisitTime({ scheduled_at, event_end_time }) {
  const from = scheduled_at?.slice(11, 16) || '';
  let to = null;

  if (event_end_time) {
    to = event_end_time.slice(0, 5);
  } else if (from) {
    to = addMinutesHHMM(from, 45);
  }

  return (
    <span className="font-mono text-base">
      {from && to ? `${from}–${to}` : from || '-'}
    </span>
  );
}

function ParentVisitsToday() {
  const [visits, setVisits] = useState([]);
  const [serverDateStr, setServerDateStr] = useState(null);

  const [showNotesHistory, setShowNotesHistory] = useState(false);
  const [notesHistory, setNotesHistory] = useState([]);
  const [notesHistoryLoading, setNotesHistoryLoading] = useState(false);
  const [notesHistoryStudent, setNotesHistoryStudent] = useState(null);

  // Weather (OPS / W2U)
  const [opsWeather, setOpsWeather] = useState(null); // normalized view model
  const [opsWeatherLoading, setOpsWeatherLoading] = useState(false);

  const normalizeOpsWeather = (payload, dateStr) => {
    const root = payload?.data || payload || {};
    const cityData = root?.cityData || root?.data?.cityData || null;

    const mapToArray = (obj, dtField = 'dt') => {
      if (!obj || typeof obj !== 'object') return [];
      return Object.entries(obj).map(([k, v]) => ({ ...(v || {}), [dtField]: k }));
    };

    // Hourly
    let hourlyList = [];
    if (cityData?.hourly) {
      hourlyList = Array.isArray(cityData.hourly) ? cityData.hourly : mapToArray(cityData.hourly, 'dt');
    } else if (root?.hourly) {
      hourlyList = Array.isArray(root.hourly) ? root.hourly : mapToArray(root.hourly, 'dt');
    } else if (root?.data?.hourly) {
      hourlyList = Array.isArray(root.data.hourly) ? root.data.hourly : mapToArray(root.data.hourly, 'dt');
    }

    // Daily
    let dailyList = [];
    if (cityData?.daily) {
      dailyList = Array.isArray(cityData.daily) ? cityData.daily : mapToArray(cityData.daily, 'date');
    } else if (root?.daily) {
      dailyList = Array.isArray(root.daily) ? root.daily : mapToArray(root.daily, 'date');
    } else if (root?.data?.daily) {
      dailyList = Array.isArray(root.data.daily) ? root.data.daily : mapToArray(root.data.daily, 'date');
    }

    const getStamp = (x) =>
      String(x?.dt || x?.date || x?.time || x?.timestamp || x?.valid || '');

    let d = dailyList.find((x) => getStamp(x).slice(0, 10) === dateStr) || dailyList[0] || null;

    const todaysHourly = (hourlyList || []).filter((h) => getStamp(h).slice(0, 10) === dateStr);

    if (!d) {
      if (!todaysHourly.length) return null;

      const temps = todaysHourly
        .map((h) => pickNumber(h, ['tmp', 'temp', 'temperature', 't', 't2m']))
        .filter((n) => n != null);

      const tminC = temps.length ? Math.min(...temps) : null;
      const tmaxC = temps.length ? Math.max(...temps) : null;

      const iconRaw = (todaysHourly.find((h) => h?.icon)?.icon || '').toString().trim() || null;

      d = {
        date: dateStr,
        icon: iconRaw,
        tmin: tminC,
        tmax: tmaxC,
      };
    }

    const iconDaily = normalizeIconFilename(d.icon || d.ico || d.weather_icon);

    const tmaxC =
      pickNumber(d, ['tmax', 'tmax_c', 'max', 'tmp_max', 'temp_max', 'hi', 'tHigh']) ??
      pickNumber(d, ['tmp', 'temp', 'temperature', 't']) ??
      null;

    const tminC =
      pickNumber(d, ['tmin', 'tmin_c', 'min', 'tmp_min', 'temp_min', 'lo', 'tLow']) ??
      pickNumber(d, ['tmp', 'temp', 'temperature', 't']) ??
      null;

    let alertCode = null;
    let alertHour = null;

    for (const h of todaysHourly) {
      const code = getAlertCode(h);
      if (code) {
        alertCode = code;
        alertHour = parseHourLabel(getStamp(h));
        break;
      }
    }

    const alertIcon = alertCode ? normalizeIconFilename(`${alertCode}`) : null;

    return {
      date: dateStr,
      iconDaily,
      tmaxC,
      tminC,
      alertCode,
      alertHour,
      alertIcon,
    };
  };

  const fetchOpsWeather = async () => {
    if (!serverDateStr) return;
    if (!navigator.onLine) {
      setOpsWeather(null);
      return;
    }

    setOpsWeatherLoading(true);
    try {
      const res = await API.get('/ops-weather/san-diego');
      if (res?.data?.empty) {
        setOpsWeather(null);
        return;
      }
      const normalized = normalizeOpsWeather(res.data, serverDateStr);
      setOpsWeather(normalized || null);
    } catch {
      setOpsWeather(null);
    } finally {
      setOpsWeatherLoading(false);
    }
  };

  useEffect(() => {
    API.get('/server-time').then((res) => {
      setServerDateStr(res.data.serverTime.slice(0, 10));
    });
  }, []);

  useEffect(() => {
    if (serverDateStr) fetchTodayVisits(serverDateStr);
    // eslint-disable-next-line
  }, [serverDateStr]);

  // Weather effect (refresh every 10 min while open)
  useEffect(() => {
    if (!serverDateStr) return;

    fetchOpsWeather();

    const t = setInterval(() => {
      if (navigator.onLine) fetchOpsWeather();
    }, 10 * 60 * 1000);

    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [serverDateStr]);

  const fetchTodayVisits = async (dateStr) => {
    try {
      const params = { date_from: dateStr, date_to: dateStr };
      const res = await API.get('/parent/visits', { params });
      setVisits(res.data || []);
    } catch (err) {
      console.error('Failed to load parent visits:', err);
      setVisits([]);
    }
  };

  const grouped = groupByDate(visits);

  const openNotesHistoryModal = async (studentId, studentName) => {
    setNotesHistoryLoading(true);
    setShowNotesHistory(true);
    setNotesHistoryStudent(studentName);
    try {
      const res = await API.get(`/students/${studentId}/notes-history`);
      setNotesHistory(res.data || []);
    } catch {
      setNotesHistory([]);
    } finally {
      setNotesHistoryLoading(false);
    }
  };

  // Weather widget (desktop)
  const WeatherWidget = () => {
    if (!navigator.onLine) return null;

    if (opsWeatherLoading) {
      return (
        <div className="hidden sm:flex items-center gap-2 px-3 py-1 rounded-full border bg-white text-xs text-slate-500">
          <span className="inline-block w-2 h-2 rounded-full bg-slate-300" />
          Weather…
        </div>
      );
    }
    if (!opsWeather) return null;

    const dailySrc = opsWeatherIconSrc(opsWeather.iconDaily);
    const alertSrc = opsWeather.alertIcon ? opsWeatherIconSrc(opsWeather.alertIcon) : null;

    const tmaxColor = getTemperatureColorCelsius(opsWeather.tmaxC);
    const tminColor = getTemperatureColorCelsius(opsWeather.tminC);

    return (
      <button
        type="button"
        onClick={() => window.open(W2U_SD_URL, '_blank', 'noopener,noreferrer')}
        className="hidden sm:flex items-center gap-2 px-3 py-1 rounded-full border bg-white hover:bg-slate-50 transition-colors"
        title="Open Weather2Umbrella (San Diego)"
      >
        {dailySrc ? (
          <img src={dailySrc} alt="weather" className="w-6 h-6 object-contain" />
        ) : (
          <span className="text-xs text-slate-500">Weather</span>
        )}

        <div className="flex items-center gap-1 text-xs font-semibold">
          <span style={{ color: tminColor }}>{formatF(opsWeather.tminC)}</span>
          <span className="text-slate-400">/</span>
          <span style={{ color: tmaxColor }}>{formatF(opsWeather.tmaxC)}</span>
        </div>

        {alertSrc ? (
          <div className="flex items-center gap-1 ml-1">
            <img src={alertSrc} alt="alert" className="w-5 h-5 object-contain" />
            {opsWeather.alertHour ? (
              <span className="text-[11px] font-semibold text-slate-700">{opsWeather.alertHour}</span>
            ) : null}
          </div>
        ) : null}
      </button>
    );
  };

  return (
    <div className="w-full px-2 sm:px-4">
      {/* Header + Weather (desktop) */}
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-bold">Today&apos;s Visits</h2>
          <WeatherWidget />
        </div>
      </div>

      {/* Mobile weather pill */}
      {navigator.onLine && opsWeather && (
        <button
          type="button"
          onClick={() => window.open(W2U_SD_URL, '_blank', 'noopener,noreferrer')}
          className="sm:hidden mb-3 w-full flex items-center justify-between gap-2 px-3 py-2 rounded-xl border bg-white hover:bg-slate-50"
          title="Open Weather2Umbrella (San Diego)"
        >
          <div className="flex items-center gap-2">
            {opsWeatherIconSrc(opsWeather.iconDaily) ? (
              <img
                src={opsWeatherIconSrc(opsWeather.iconDaily)}
                alt="weather"
                className="w-7 h-7 object-contain"
              />
            ) : null}
            <div className="flex flex-col items-start">
              <div className="text-xs font-semibold text-slate-800">San Diego</div>
              <div className="text-[11px] text-slate-500">Tap to open forecast</div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="text-xs font-semibold">
              <span style={{ color: getTemperatureColorCelsius(opsWeather.tminC) }}>
                {formatF(opsWeather.tminC)}
              </span>
              <span className="text-slate-400 mx-1">/</span>
              <span style={{ color: getTemperatureColorCelsius(opsWeather.tmaxC) }}>
                {formatF(opsWeather.tmaxC)}
              </span>
            </div>

            {opsWeather.alertIcon && opsWeatherIconSrc(opsWeather.alertIcon) ? (
              <div className="flex items-center gap-1">
                <img
                  src={opsWeatherIconSrc(opsWeather.alertIcon)}
                  alt="alert"
                  className="w-6 h-6 object-contain"
                />
                {opsWeather.alertHour ? (
                  <span className="text-[11px] font-semibold text-slate-700">{opsWeather.alertHour}</span>
                ) : null}
              </div>
            ) : null}
          </div>
        </button>
      )}

      {!serverDateStr ? (
        <div className="p-4 text-gray-500 text-center">Loading server time...</div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="text-gray-500 mt-6">There are no scheduled visits for today.</div>
      ) : (
        Object.entries(grouped).map(([date, group]) => (
          <div key={date} className="mb-6 shadow rounded-lg bg-white border">
            <div className="font-bold text-base px-4 py-3 bg-gray-50 rounded-t-lg flex items-center">
              <span>{date}</span>
              <span className="ml-3 bg-blue-200 text-blue-900 text-xs px-2 py-0.5 rounded-full">
                {group.length} visits
              </span>
            </div>

            {/* DESKTOP */}
            <div className="overflow-x-auto hidden sm:block">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-2 py-1 text-left">Time</th>
                    <th className="px-2 py-1 text-left">Child</th>
                    <th className="px-2 py-1 text-left">Provider</th>
                    <th className="px-2 py-1 text-left">Status</th>
                    <th className="px-2 py-1 text-center">G-sign</th>
                  </tr>
                </thead>
                <tbody>
                  {group.map((v) => {
                    const eventFlag = isEventVisit(v);
                    return (
                      <tr key={v.visit_id} className="hover:bg-gray-50">
                        <td className="px-2 py-1">
                          <VisitTime scheduled_at={v.scheduled_at} event_end_time={v.event_end_time} />
                        </td>
                        <td className="px-2 py-1">
                          <div className="flex flex-col gap-0.5">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span>{v.student_name}</span>
                              {/* DISCIPLINE vs EVENT */}
                              {eventFlag ? (
                                <EventBadge isEvent={true} />
                              ) : (
                                <ProgramBadge program_type={v.program_type} discipline_code={v.discipline_code} />
                              )}
                              <button
                                type="button"
                                title="Show Notes History"
                                className="text-purple-700 hover:text-purple-900 p-1"
                                onClick={() => openNotesHistoryModal(v.student_id, v.student_name)}
                              >
                                <FaBookOpen size={16} />
                              </button>
                            </div>

                            {/* Telehealth badge + join link ispod imena */}
                            <TelehealthBadge
                              delivery_mode={v.delivery_mode}
                              telehealth_link={v.telehealth_link}
                              showLink={true}
                            />
                          </div>
                        </td>
                        <td className="px-2 py-1">
                          <span>{v.teacher_name}</span>
                          {v.teacher_id !== v.original_teacher_id && v.original_teacher_id && (
                            <div className="text-xs text-gray-400 mt-1">Substitute provider</div>
                          )}
                        </td>
                        <td className="px-2 py-1">
                          <StatusBadge status={v.status}>{v.status}</StatusBadge>
                          {v.status === 'canceled' && v.notes && (
                            <div className="text-xs text-purple-700 mt-1 italic">{v.notes}</div>
                          )}
                        </td>
                        <td className="px-2 py-1 text-center">
                          {v.g_sign_confirmed ? (
                            <span className="text-green-600 text-md">✅</span>
                          ) : (
                            <span className="text-red-600 text-md">❌</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* MOBILNI – kartice */}
            <div className="sm:hidden flex flex-col gap-2 p-2">
              {group.map((v) => {
                const eventFlag = isEventVisit(v);
                return (
                  <div key={v.visit_id} className="rounded-2xl border shadow p-3 mb-1 bg-white">
                    <div className="flex justify-between items-center gap-2">
                      <div className="flex-1">
                        <div className="flex flex-col gap-0.5">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold">{v.student_name}</span>
                            {eventFlag ? (
                              <EventBadge isEvent={true} />
                            ) : (
                              <ProgramBadge program_type={v.program_type} discipline_code={v.discipline_code} />
                            )}
                            <button
                              onClick={() => openNotesHistoryModal(v.student_id, v.student_name)}
                              className="ml-1 text-purple-700 hover:text-purple-900"
                              title="Show Notes History"
                            >
                              <FaBookOpen size={16} />
                            </button>
                          </div>

                          {/* Telehealth badge + join link ispod imena */}
                          <TelehealthBadge
                            delivery_mode={v.delivery_mode}
                            telehealth_link={v.telehealth_link}
                            showLink={true}
                          />
                        </div>

                        <div className="mt-1 text-xs text-gray-600">
                          Provider: {v.teacher_name}
                          {v.teacher_id !== v.original_teacher_id && v.original_teacher_id && (
                            <span className="ml-1 text-gray-400">(Substitute)</span>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <VisitTime scheduled_at={v.scheduled_at} event_end_time={v.event_end_time} />
                      </div>
                    </div>

                    <div className="flex justify-between items-center mt-2">
                      <StatusBadge status={v.status}>{v.status}</StatusBadge>
                      <span className="text-xs text-gray-700">
                        G-sign:{' '}
                        {v.g_sign_confirmed ? (
                          <span className="text-green-600">Yes</span>
                        ) : (
                          <span className="text-red-600">No</span>
                        )}
                      </span>
                    </div>

                    {v.status === 'canceled' && v.notes && (
                      <div className="mt-1 text-xs text-purple-700 italic">{v.notes}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}

      {/* Notes history modal – isti format kao za admin, sa domain notes */}
      {showNotesHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-5 rounded shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-bold">Notes History: {notesHistoryStudent}</h3>
              <button className="text-gray-500" onClick={() => setShowNotesHistory(false)}>
                ✕
              </button>
            </div>

            {notesHistoryLoading ? (
              <div className="p-4 text-center text-gray-400">Loading...</div>
            ) : notesHistory.length === 0 ? (
              <div className="p-4 text-center text-gray-500">No notes found for this student.</div>
            ) : (
              <div className="space-y-4">
                {notesHistory.map((entry, idx) => (
                  <div key={idx} className="border rounded p-3 bg-gray-50">
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex flex-col">
                        <span className="font-semibold text-blue-900">{entry.teacher_name}</span>
                        {entry.discipline_name && (
                          <span className="text-xs text-gray-500">{entry.discipline_name}</span>
                        )}
                      </div>
                      <span className="text-xs text-gray-500">
                        {entry.scheduled_at ? entry.scheduled_at.slice(0, 16).replace('T', ' ') : ''}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                      {entry.domains && entry.domains.length > 0 ? (
                        entry.domains.map((dom) =>
                          dom.note && dom.note.trim() !== '' ? (
                            <div key={dom.domain_id}>
                              <span className="font-semibold">{dom.name || dom.code || 'Domain'}:</span>{' '}
                              <span>{dom.note}</span>
                            </div>
                          ) : null
                        )
                      ) : (
                        <span className="text-xs text-gray-500 italic">No domain notes for this visit.</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ParentVisitsToday;




